import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Database, FileCheck, CreditCard, CandlestickChart, Mail, MessageSquare, Phone, Network, Activity, FlaskConical,
} from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/constants";

const ICONS = {
  crm: Database, kyc: FileCheck, payments: CreditCard, mt5: CandlestickChart,
  email: Mail, sms: MessageSquare, whatsapp: MessageSquare, voip: Phone,
};

export default function Integrations() {
  const [items, setItems] = useState(null);
  const [testing, setTesting] = useState("");

  useEffect(() => {
    api.get("/integrations").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const runDiagnostic = (item) => {
    setTesting(item.key);
    setTimeout(() => {
      setTesting("");
      toast.success(`${item.name} diagnostic passed`, {
        description: `Simulated handshake OK · ${item.latency_ms}ms · demo connection, no live traffic.`,
      });
    }, 700);
  };

  if (!items) {
    return <div className="font-mono text-zinc-500 text-sm animate-pulse" data-testid="integrations-loading">LOADING INTEGRATIONS…</div>;
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <Network className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Integrations
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          All connections below are <span className="text-emerald-400 font-semibold">simulated demo connections</span> for this synthetic environment. No real external systems are connected.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((item, i) => {
          const Icon = ICONS[item.key] || Network;
          return (
            <div
              key={item.key}
              data-testid={`integration-card-${item.key}`}
              style={{ animationDelay: `${i * 40}ms` }}
              className="fade-slide-in bg-zinc-900 border border-zinc-800 rounded-md p-5 hover:border-zinc-700 hover:-translate-y-[2px] transition-all duration-200 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
                </div>
                <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-400" data-testid={`integration-status-${item.key}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  Simulated Connection
                </span>
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-100">{item.name}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{item.vendor}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div>
                  <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Latency</div>
                  <div className="text-zinc-300">{item.latency_ms}ms</div>
                </div>
                <div>
                  <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Events today</div>
                  <div className="text-zinc-300">{item.events_today.toLocaleString()}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Last sync</div>
                  <div className="text-zinc-300">{timeAgo(item.last_sync)}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono text-amber-500/90 border border-amber-500/20 bg-amber-500/5 rounded-sm px-2 py-1.5">
                <FlaskConical className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                {item.mode}
              </div>
              <button
                data-testid={`integration-test-${item.key}`}
                onClick={() => runDiagnostic(item)}
                disabled={testing === item.key}
                className="mt-auto flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 transition-colors duration-200 disabled:opacity-60"
              >
                <Activity className={`w-3.5 h-3.5 text-emerald-400 ${testing === item.key ? "animate-pulse" : ""}`} strokeWidth={1.5} />
                {testing === item.key ? "Running…" : "Run Diagnostic"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="integrations-note">
        <h3 className="text-sm font-semibold text-zinc-200 mb-2">Production deployment</h3>
        <p className="text-xs text-zinc-400 leading-relaxed max-w-3xl">
          In production, O.N.E.Tech connects to your real Broker CRM, KYC provider, payment orchestration layer, MT5 Manager API and
          messaging gateways via authenticated, encrypted connectors with per-jurisdiction routing. This demonstration environment uses
          synthetic event streams only — the vendor names above are illustrative placeholders, and no credentials, live traffic or real
          client data exist in this sandbox.
        </p>
      </div>
    </div>
  );
}
